import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useNotificationList,
  useMarkAsRead,
  useMarkAllRead,
  useDeleteNotification,
  useClearReadNotifications,
} from '../hooks/useNotifications';
import type { Notification as AppNotification } from '../types/notification.types';
import NotificationItem from './NotificationItem';

type NotificationDropdownVariant = 'popover' | 'panel' | 'dock';
type NotificationDropdownTheme = 'default' | 'edgemail';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  variant?: NotificationDropdownVariant;
  theme?: NotificationDropdownTheme;
}

export default function NotificationDropdown({
  isOpen,
  onClose,
  variant = 'popover',
  theme = 'default',
}: NotificationDropdownProps) {
  const navigate = useNavigate();
  const [showRead, setShowRead] = useState(false);
  const { data, isLoading } = useNotificationList({ limit: 15, unreadOnly: !showRead });
  const markAsRead = useMarkAsRead();
  const markAllRead = useMarkAllRead();
  const deleteNotification = useDeleteNotification();
  const clearRead = useClearReadNotifications();

  const notifications: AppNotification[] = data?.notifications ?? [];

  const handleMarkRead = useCallback((id: string) => {
    markAsRead.mutate(id);
  }, [markAsRead]);

  const handleNavigate = useCallback((link: string) => {
    onClose();
    navigate(link);
  }, [navigate, onClose]);

  const handleMarkAllRead = useCallback(() => {
    markAllRead.mutate();
  }, [markAllRead]);

  const handleDelete = useCallback((id: string) => {
    deleteNotification.mutate(id);
  }, [deleteNotification]);

  const handleClearRead = useCallback(() => {
    clearRead.mutate();
  }, [clearRead]);

  if (!isOpen) return null;

  const isPanel = variant === 'panel';
  const isDock = variant === 'dock';
  const isEdgeMail = theme === 'edgemail';

  const containerClassName = isDock
    ? 'h-full w-full flex flex-col min-h-0 overflow-hidden'
    : isPanel
      ? `fixed right-4 top-[5.25rem] bottom-4 w-[360px] max-w-[calc(100vw-2rem)] flex flex-col z-50 overflow-hidden ${
          isEdgeMail
            ? 'bg-[var(--em-surface-card)] border border-[var(--em-border-primary)] rounded-2xl shadow-2xl em-smooth'
            : 'bg-[var(--brand-surface-card)] border border-[var(--brand-border)] rounded-2xl shadow-2xl'
        }`
      : `absolute right-0 top-full mt-2 w-80 sm:w-96 z-50 overflow-hidden ${
          isEdgeMail
            ? 'bg-[var(--em-surface-card)] border border-[var(--em-border-primary)] rounded-xl shadow-xl em-smooth'
            : 'bg-[var(--brand-surface-card)] border border-[var(--brand-border)] rounded-lg shadow-xl'
        }`;

  const headerClassName = isEdgeMail
    ? 'flex items-center justify-between px-4 py-2.5 border-b border-[var(--em-border-primary)]'
    : 'flex items-center justify-between px-4 py-2.5 border-b border-[var(--brand-border)]';

  const titleClassName = isEdgeMail
    ? 'text-sm font-semibold text-[var(--em-text-primary)]'
    : 'text-sm font-semibold text-[var(--brand-text-primary)]';

  const markAllClassName = isEdgeMail
    ? 'flex items-center gap-1 text-xs text-[var(--em-accent-primary)] hover:text-[var(--em-accent-primary-hover)]'
    : 'flex items-center gap-1 text-xs text-[var(--brand-accent-primary)] hover:text-[var(--brand-accent-primary-hover)]';

  const toggleReadClassName = isEdgeMail
    ? 'text-xs text-[var(--em-text-tertiary)] hover:text-[var(--em-text-primary)]'
    : 'text-xs text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)]';

  const clearReadClassName = isEdgeMail
    ? 'text-xs text-[var(--em-status-danger-text)] hover:text-[var(--em-status-danger-text)]'
    : 'text-xs text-red-600 hover:text-red-700';

  const listClassName = isPanel || isDock
    ? isEdgeMail
      ? 'flex-1 overflow-y-auto divide-y divide-[var(--em-border-secondary)]'
      : 'flex-1 overflow-y-auto divide-y divide-[var(--brand-border)]'
    : isEdgeMail
      ? 'max-h-[400px] overflow-y-auto divide-y divide-[var(--em-border-secondary)]'
      : 'max-h-[400px] overflow-y-auto divide-y divide-[var(--brand-border)]';

  return (
    <div className={containerClassName}>
      {/* Header */}
      <div className={headerClassName}>
        <h3 className={titleClassName}>Notifications</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleMarkAllRead}
            className={markAllClassName}
            title="Mark all as read"
          >
            <CheckIcon className="w-3.5 h-3.5" />
            Mark all read
          </button>
          <button
            onClick={() => setShowRead((prev) => !prev)}
            className={toggleReadClassName}
            title={showRead ? 'Hide read notifications' : 'Show read notifications'}
          >
            {showRead ? 'Hide read' : 'Show read'}
          </button>
          {showRead && (
            <button
              onClick={handleClearRead}
              className={clearReadClassName}
              title="Clear read notifications"
            >
              Clear read
            </button>
          )}
          {(isPanel || isDock) && (
            <button
              onClick={onClose}
              className={isEdgeMail ? 'p-1 rounded-md text-[var(--em-text-tertiary)] hover:text-[var(--em-text-primary)] hover:bg-[var(--em-state-hover)]' : 'p-1 rounded-md text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-secondary)] hover:bg-[var(--brand-surface-hover)]'}
              title="Close"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className={listClassName}>
        {isLoading ? (
          <div className={`px-4 py-8 text-center text-sm ${isEdgeMail ? 'text-[var(--em-text-tertiary)]' : 'text-[var(--brand-text-tertiary)]'}`}>Loading...</div>
        ) : notifications.length === 0 ? (
          <div className={`px-4 py-8 text-center text-sm ${isEdgeMail ? 'text-[var(--em-text-tertiary)]' : 'text-[var(--brand-text-tertiary)]'}`}>No notifications yet</div>
        ) : (
          notifications.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onMarkRead={handleMarkRead}
              onNavigate={handleNavigate}
              onDelete={handleDelete}
              theme={theme}
            />
          ))
        )}
      </div>
    </div>
  );
}
